import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { LabProvider } from '../../contexts/LabContext';
import { QCMProvider } from '../../contexts/QCMContext';
import LabViewer from '../lab/LabViewer';
import QCMViewer from '../qcm/QCMViewer';
import MobileQCM from '../mobile/MobileQCM';
import MobileDashboard from '../mobile/MobileDashboard';
import { nodeApi, fetchAuthorizedExams, createSessionApi } from '../../services/api';
import { resolveFirstQcmId, NO_QUESTION_FOR_STRATEGY_MESSAGE } from '../../utils/qcmInitialLoad';
import { readGuestQcmSession } from '../../utils/guestSession';

/** Vue panneaux (QCMViewer) vs plein écran (MobileQCM). Sur bureau : panneaux par défaut (comportement d’origine). */
function readDesktopQcmModeFromEnvironment() {
    if (typeof window === 'undefined') return true;
    try {
        const p = new URLSearchParams(window.location.search);
        if (p.get('mobile') === '1' || window.localStorage?.getItem('forceMobile') === '1') {
            return false;
        }
        if (p.get('desktop') === '0') {
            return false;
        }
        if (p.get('desktop') === '1' || window.localStorage?.getItem('useDesktopQcmViewer') === '1') {
            return true;
        }
        const ua = (navigator.userAgent || navigator.vendor || window.opera || '').toLowerCase();
        const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
        const hasCoarsePointer = window.matchMedia?.('(pointer: coarse)')?.matches;
        return !(isMobileDevice || hasCoarsePointer);
    } catch {
        return true;
    }
}

/** IDs inventés côté navigateur (jamais créés en Mongo) → 404 Session not found. */
function isClientOnlySessionId(sessionId) {
    const s = String(sessionId || '').trim();
    if (!s) return true;
    if (s === '111222' || s === 'default_session' || s === 'default-session') return true;
    return s.startsWith('sess_');
}

function activeSessionStorageKey(userId, examId) {
    const u = String(userId || 'learner1').trim();
    const e = String(examId || 'pending').trim();
    return `active_qcm_session_${u}_${e}`;
}

function resolveRuntimeSessionId(userObj, userId, examId) {
    const persisted = String(userObj?.lastSelections?.sessionId || '').trim();
    if (persisted && !isClientOnlySessionId(persisted)) {
        return persisted;
    }
    const key = activeSessionStorageKey(userId, examId);
    try {
        const existing = window.sessionStorage.getItem(key);
        if (existing && String(existing).trim() && !isClientOnlySessionId(existing)) {
            return String(existing).trim();
        }
    } catch {
        /* ignore */
    }
    return '';
}

const SmartExamRouter = ({ renderMobile }) => {
    const { user, isGuest, loading: authLoading, saveSelections } = useAuth();
    // Le compte « guest » auto-loginé n'a pas de `lastSelections` propres
    // (il est partagé). On consomme donc la sélection écrite par
    // PublicExamHomeScreen → handlePickGuest dans le localStorage, exactement
    // comme pour un visiteur anonyme.
    const isPublicVisitor = !user || isGuest;
    const [examType, setExamType] = useState(null);
    const [qcmId, setQcmId] = useState(null); // NOUVEAU: Stocker le QCM ID spécifique
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    /** Session Mongo réelle (UUID) — remplace les anciens IDs locaux `sess_*`. */
    const [ensuredSessionId, setEnsuredSessionId] = useState('');
    const ensuringSessionRef = useRef(false);
    const [isMobile, setIsMobile] = useState(false);
    const [forceMobile, setForceMobile] = useState(false);
    /** Overlay mobile : dashboard par-dessus le QCM (ne pas démonter MobileQCM). */
    const [mobileOverlay, setMobileOverlay] = useState(null);
    /** Premier chargement : aucun QCM pour la stratégie → monter quand même MobileQCM (bandeau + réglages). */
    const [strategyNoQuestionBootstrap, setStrategyNoQuestionBootstrap] = useState(false);

    const [desktopQcmMode, setDesktopQcmMode] = useState(readDesktopQcmModeFromEnvironment);
    useEffect(() => {
        const readDesktopMode = () => setDesktopQcmMode(readDesktopQcmModeFromEnvironment());
        readDesktopMode();
        window.addEventListener('locationchange', readDesktopMode);
        window.addEventListener('popstate', readDesktopMode);
        return () => {
            window.removeEventListener('locationchange', readDesktopMode);
            window.removeEventListener('popstate', readDesktopMode);
        };
    }, []);

    useEffect(() => {
        const onNav = (e) => {
            const page = e.detail?.page;
            if (page === 'dashboard') setMobileOverlay('dashboard');
            else setMobileOverlay(null);
        };
        window.addEventListener('nav_request', onNav);
        return () => window.removeEventListener('nav_request', onNav);
    }, []);

    // Détection mobile - SEULEMENT basée sur l'User Agent, pas sur la taille d'écran
    useEffect(() => {
        const checkMobile = () => {
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
            const hasCoarsePointer = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
            
            // Query param override (?mobile=1) or localStorage.forceMobile === '1'
            const params = new URLSearchParams(window.location.search);
            const forceParam = params.get('mobile') === '1';
            const forceLS = (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('forceMobile') === '1');
            const shouldForce = forceParam || forceLS || false;
            setForceMobile(shouldForce);

            // Avoid false positives on desktop: do not rely on small screen alone
            const detectedMobile = isMobileDevice || hasCoarsePointer;
            setIsMobile(shouldForce ? true : detectedMobile);
        };

        // Initial and resize
        checkMobile();
        window.addEventListener('resize', checkMobile);

        // React to URL changes (best effort without a router): pushState/replaceState/popstate/hashchange
        const origPushState = window.history.pushState;
        const origReplaceState = window.history.replaceState;
        const emitLocationChange = () => window.dispatchEvent(new Event('locationchange'));
        window.history.pushState = function() {
            const ret = origPushState.apply(this, arguments);
            emitLocationChange();
            return ret;
        };
        window.history.replaceState = function() {
            const ret = origReplaceState.apply(this, arguments);
            emitLocationChange();
            return ret;
        };
        const handleLocationChange = () => checkMobile();
        window.addEventListener('popstate', handleLocationChange);
        window.addEventListener('hashchange', handleLocationChange);
        window.addEventListener('locationchange', handleLocationChange);

        return () => {
            window.removeEventListener('resize', checkMobile);
            window.removeEventListener('popstate', handleLocationChange);
            window.removeEventListener('hashchange', handleLocationChange);
            window.removeEventListener('locationchange', handleLocationChange);
            window.history.pushState = origPushState;
            window.history.replaceState = origReplaceState;
        };
    }, []);

    const guestSessionForContext = useMemo(() => {
        if (authLoading) return null;
        if (!isPublicVisitor) return null;
        return readGuestQcmSession();
    }, [authLoading, isPublicVisitor]);

    const sessionUserId = isPublicVisitor
        ? guestSessionForContext?.examId
            ? 'guest'
            : ''
        : String(user.username || user.email || user.id || user._id || '').trim();

    const selectedExam = isPublicVisitor
        ? guestSessionForContext?.examId
        : user?.lastSelections?.examId ?? guestSessionForContext?.examId;
    const selectedStrategy = isPublicVisitor
        ? guestSessionForContext?.strategyId || 'random'
        : user?.lastSelections?.strategyId || 'random';
    const resolvedSessionId = resolveRuntimeSessionId(
        isPublicVisitor ? {} : user,
        sessionUserId || 'learner1',
        selectedExam
    );
    const runtimeSessionId = ensuredSessionId || resolvedSessionId;

    // Construire le contexte de session
    const sessionContext = {
        sessionId: runtimeSessionId,
        user: sessionUserId || 'learner1',
        exam: selectedExam,
        strategy: selectedStrategy,
    };

    /** Créer une session Mongo réelle si on n'a qu'un ID local `sess_*` (évite 404 Session not found). */
    useEffect(() => {
        if (authLoading) return;
        if (!selectedExam) return;
        if (runtimeSessionId && !isClientOnlySessionId(runtimeSessionId)) return;
        if (ensuringSessionRef.current) return;

        let cancelled = false;
        ensuringSessionRef.current = true;
        (async () => {
            try {
                const shortLabel = String(selectedExam).slice(0, 12) || 'practice';
                const created = await createSessionApi({
                    examId: selectedExam,
                    strategyId: selectedStrategy || 'random',
                    name: shortLabel,
                });
                const sid = String(created?.session?.sessionId || '').trim();
                if (!sid || cancelled) return;
                try {
                    window.sessionStorage.setItem(
                        activeSessionStorageKey(sessionUserId || 'learner1', selectedExam),
                        sid
                    );
                } catch {
                    /* ignore */
                }
                setEnsuredSessionId(sid);
                if (!isPublicVisitor && typeof saveSelections === 'function') {
                    saveSelections({
                        examId: selectedExam,
                        strategyId: selectedStrategy || 'random',
                        sessionId: sid,
                    }).catch(() => {});
                }
            } catch (e) {
                console.warn('ensure backend session failed:', e?.message || e);
            } finally {
                ensuringSessionRef.current = false;
            }
        })();

        return () => {
            cancelled = true;
            ensuringSessionRef.current = false;
        };
    }, [
        authLoading,
        selectedExam,
        selectedStrategy,
        runtimeSessionId,
        sessionUserId,
        isPublicVisitor,
        saveSelections,
    ]);

    /** Clé examen + stratégie : doit invalider le cache si l’une des deux change */
    const lastFetchedExamStrategyKeyRef = useRef(null);

    /**
     * Changement d’examen ou de stratégie (profil) : nouvelle question selon la stratégie + rechargement UI.
     * useLayoutEffect évite un flash question ancienne + libellé stratégie nouveau.
     */
    const prevSessionKeyRef = useRef(null);
    useLayoutEffect(() => {
        const examId = isPublicVisitor
            ? guestSessionForContext?.examId
            : user?.lastSelections?.examId ?? guestSessionForContext?.examId;
        const strategyId = isPublicVisitor
            ? guestSessionForContext?.strategyId || 'random'
            : user?.lastSelections?.strategyId || 'random';
        const sessionId = runtimeSessionId;
        const k = `${examId || ''}|${strategyId}|${sessionId || ''}`;
        if (prevSessionKeyRef.current === null) {
            prevSessionKeyRef.current = k;
            return;
        }
        if (prevSessionKeyRef.current !== k) {
            prevSessionKeyRef.current = k;
            lastFetchedExamStrategyKeyRef.current = null;
            setQcmId(null);
            setStrategyNoQuestionBootstrap(false);
            setLoading(true);
        }
    }, [
        isPublicVisitor,
        user?.lastSelections?.examId,
        user?.lastSelections?.strategyId,
        user?.lastSelections?.sessionId,
        guestSessionForContext?.examId,
        guestSessionForContext?.strategyId,
        runtimeSessionId,
    ]);

    /** Pas d'examen en session → retour catalogue (évite « Loading exam… » + Exam: — à l'infini). */
    useEffect(() => {
        if (authLoading) return;
        const guestLive = isPublicVisitor ? readGuestQcmSession() : null;
        const isAuthed =
            !isPublicVisitor && !!(user && (user.username || user.email || user.id || user._id));
        const examId = isPublicVisitor
            ? guestLive?.examId
            : user?.lastSelections?.examId ?? guestLive?.examId;
        if (examId) return;
        setLoading(false);
        setError(null);
        try {
            localStorage.setItem('adaptive_exam_entry', 'picker');
        } catch {
            /* ignore */
        }
        window.dispatchEvent(new CustomEvent('nav_request', { detail: { page: 'home' } }));
    }, [authLoading, isPublicVisitor, user, guestSessionForContext?.examId]);

    useEffect(() => {
        const detectExamType = async () => {
            if (authLoading) {
                setLoading(true);
                return;
            }

            const guestLive = isPublicVisitor ? readGuestQcmSession() : null;
            const isAuthed = !isPublicVisitor && !!(user && (user.username || user.email || user.id || user._id));
            if (!isAuthed && !(guestLive && guestLive.examId)) {
                setLoading(false);
                return;
            }

            if (!sessionContext.exam) {
                setError('Aucun examen sélectionné');
                setLoading(false);
                return;
            }

            // Attendre la création Mongo (évite QCM + GET session avec un ID `sess_*` fantôme).
            if (!sessionContext.sessionId || isClientOnlySessionId(sessionContext.sessionId)) {
                setLoading(true);
                return;
            }

            const fetchKey = `${sessionContext.exam}|${sessionContext.strategy}`;

            try {
                setLoading(true);
                setError(null);

                if (lastFetchedExamStrategyKeyRef.current === fetchKey) {
                    setLoading(false);
                    return;
                }

                let type = 'qcm';
                if (guestLive && guestLive.examId && !isAuthed) {
                    type = guestLive.examType || 'qcm';
                } else {
                    const examsResp = await fetchAuthorizedExams();
                    const exams = (examsResp && examsResp.examens) || [];
                    const current = exams.find((e) => e.examId === sessionContext.exam);
                    type = (current && current.examType) || 'qcm';
                }

                if (type === 'qcm') {
                    // Stratégie (révision + session) → recherche → id ; pas de repli silencieux vers un autre examen
                    const resolved = await resolveFirstQcmId({
                        examId: sessionContext.exam,
                        userId: sessionContext.user,
                        sessionStrategy: sessionContext.strategy,
                        sessionId: sessionContext.sessionId,
                    });

                    if (resolved.error) {
                        if (resolved.error === NO_QUESTION_FOR_STRATEGY_MESSAGE) {
                            lastFetchedExamStrategyKeyRef.current = fetchKey;
                            setStrategyNoQuestionBootstrap(true);
                            setQcmId(null);
                            setExamType('qcm');
                            setError(null);
                            setLoading(false);
                            return;
                        }
                        setStrategyNoQuestionBootstrap(false);
                        setError(resolved.error || NO_QUESTION_FOR_STRATEGY_MESSAGE);
                        setLoading(false);
                        return;
                    }
                    if (resolved.qcmId) {
                        lastFetchedExamStrategyKeyRef.current = fetchKey;
                        setStrategyNoQuestionBootstrap(false);
                        setQcmId(resolved.qcmId);
                        setExamType('qcm');
                        setLoading(false);
                        return;
                    }
                    lastFetchedExamStrategyKeyRef.current = fetchKey;
                    setStrategyNoQuestionBootstrap(true);
                    setQcmId(null);
                    setExamType('qcm');
                    setError(null);
                    setLoading(false);
                    return;
                } else if (type === 'lab') {
                    // Lab: vérifier qu'une instruction existe
                    const labResponse = await nodeApi.get('/api/instructions/random-id', { params: { t: Date.now() } });
                    if (labResponse.data && labResponse.data.instruction_id) {
                        lastFetchedExamStrategyKeyRef.current = fetchKey;
                        setExamType('lab');
                        setLoading(false);
                        return;
                    }
                    throw new Error('No Lab instruction found for this exam');
                } else {
                    throw new Error(`Unknown examType: ${type}`);
                }

            } catch (error) {
                console.error('Error detecting exam type:', error);
                setError('Erreur lors de la détection du type d\'examen');
                setLoading(false);
            }
        };

        detectExamType();
    }, [
        authLoading,
        user,
        isPublicVisitor,
        sessionContext.exam,
        sessionContext.strategy,
        sessionContext.user,
        sessionContext.sessionId,
        guestSessionForContext?.examId,
    ]);

    if (loading) {
        return (
            <div
                className="flex items-center justify-center h-screen bg-gray-900 text-blue-400"
                style={{
                    minHeight: '100vh',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#111827',
                    color: '#93c5fd',
                }}
            >
                <div className="text-center" style={{ maxWidth: '28rem', padding: '0 1rem' }}>
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4" />
                    <p className="text-xl" style={{ fontSize: '1.125rem', margin: '0 0 0.5rem' }}>
                        Loading exam…
                    </p>
                    <p className="text-sm text-gray-400 mt-2" style={{ fontSize: '0.875rem', opacity: 0.85 }}>
                        Exam: {sessionContext.exam || '—'}
                    </p>
                </div>
            </div>
        );
    }

    if (error) {
        const isStrategyNoQuestion = error === NO_QUESTION_FOR_STRATEGY_MESSAGE;
        return (
            <div className="flex items-center justify-center h-screen bg-gray-900 text-red-400">
                <div className="text-center max-w-md px-4">
                    <div className="text-6xl mb-4">⚠️</div>
                    <h2 className="text-2xl font-bold mb-2">Erreur</h2>
                    <p className="text-lg">{error}</p>
                    {!isStrategyNoQuestion && (
                        <>
                            <p className="text-sm text-gray-400 mt-4">
                                Vérifiez votre sélection d&apos;examen sur la page d&apos;accueil
                            </p>
                            {error === 'Aucun examen sélectionné' && (
                                <button
                                    type="button"
                                    className="mt-6 px-4 py-2 rounded bg-blue-600 text-white text-sm"
                                    onClick={() =>
                                        window.dispatchEvent(
                                            new CustomEvent('nav_request', { detail: { page: 'home' } })
                                        )
                                    }
                                >
                                    Choisir un examen
                                </button>
                            )}
                        </>
                    )}
                    {isStrategyNoQuestion && (
                        <p className="text-sm text-gray-400 mt-4">
                            Ajustez la stratégie de révision ou le filtre (domaine / sous-domaine), ou choisissez un autre examen.
                        </p>
                    )}
                </div>
            </div>
        );
    }

    // Rendu conditionnel selon le type d'examen détecté
        if (examType === 'qcm') {
            const qcmSessionKey = `${sessionContext.exam}|${sessionContext.strategy}`;
            return (
                <QCMProvider
                    key={qcmSessionKey}
                    sessionContext={sessionContext}
                    initialQcmId={qcmId}
                    initialNoQuestionForStrategy={strategyNoQuestionBootstrap}
                >
                    {desktopQcmMode ? (
                        <QCMViewer />
                    ) : (
                        <>
                            <MobileQCM />
                            {mobileOverlay === 'dashboard' && (
                                <div
                                    className="fixed inset-0 z-[10000] overflow-y-auto bg-[#1a1a1a]"
                                    style={{ WebkitOverflowScrolling: 'touch' }}
                                >
                                    <MobileDashboard
                                        sessionContext={sessionContext}
                                        onClose={() => setMobileOverlay(null)}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </QCMProvider>
            );
        } else if (examType === 'lab') {
        return (
            <LabProvider sessionContext={sessionContext}>
                <LabViewer />
            </LabProvider>
        );
    }

    return (
        <div className="flex items-center justify-center h-screen bg-gray-900 text-yellow-400">
            <div className="text-center">
                <div className="text-6xl mb-4">🤔</div>
                <h2 className="text-2xl font-bold mb-2">Type d'examen inconnu</h2>
                <p className="text-lg">Impossible de déterminer le format pour: {sessionContext.exam}</p>
            </div>
        </div>
    );
};

export default SmartExamRouter;